/**
 * Version number for the offline state schema.
 *
 * INCREMENT THIS when making breaking changes that require
 * a complete reset of the offline state, including:
 * - Domain events
 * - Domain event refs (sync cursors)
 * - Projections
 *
 * This is SEPARATE from the app version (package.json version).
 * The app version tracks feature releases, while this version
 * tracks offline state compatibility.
 *
 * When this version changes, users will have their offline
 * state completely reset on next app load.
 */
export const OFFLINE_STATE_VERSION = 17

function getConfig(): NextRuntimeConfig {
  return {
    publicRuntimeConfig: {
      tusUrl:
        import.meta.env.VITE_TUS_URL ??
        'http://localhost:1080/files',
      profileImagesBaseUrl:
        import.meta.env.VITE_PROFILE_IMAGES_BASE_URL ??
        'http://localhost:1080/files/public-profiles',
      configName:
        import.meta.env.VITE_CONFIG_NAME || 'development',
    },
  }
}

export interface NextRuntimeConfig {
  publicRuntimeConfig: {
    tusUrl: string
    profileImagesBaseUrl: string
    configName: string
  }
}

export interface PublicConfigEnv {
  environment: string
  apiBaseUrl: string
  callbackBaseUrl: string
  freeagentApi: string
}

export const publicConfig = new Map<string, PublicConfigEnv>()

export function getPublicConfig(
  name?: string
): PublicConfigEnv & NextRuntimeConfig['publicRuntimeConfig'] {
  const nextConfig = getConfig() as NextRuntimeConfig
  name = name ?? nextConfig.publicRuntimeConfig.configName
  const config = publicConfig.get(name)
  if (!config) throw new Error(`Public config ${name} not found`)
  return { ...config, ...nextConfig.publicRuntimeConfig }
}

// The ports move per dev instance (see scripts/util/dev-instance.sh),
// so docker compose passes the URLs in.
publicConfig.set('development', {
  environment: 'development',
  apiBaseUrl:
    import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3041',
  callbackBaseUrl:
    import.meta.env.VITE_CALLBACK_BASE_URL ??
    'http://localhost:3070',
  freeagentApi: 'https://api.sandbox.freeagent.com',
})

publicConfig.set('preview', {
  environment: import.meta.env.VITE_SONIQ_STAGE,
  apiBaseUrl: `https://${
    import.meta.env.VITE_SONIQ_STAGE
  }-api.soniq.studio`,
  callbackBaseUrl: `https://${
    import.meta.env.VITE_SONIQ_STAGE
  }.soniq.studio`,
  freeagentApi: 'https://api.sandbox.freeagent.com',
})

publicConfig.set('production', {
  environment: 'production',
  apiBaseUrl: 'https://api.soniq.studio',
  callbackBaseUrl: 'https://soniq.studio',
  freeagentApi: 'https://api.freeagent.com',
})
